import { useState, useEffect, useMemo } from "react";
import { Outlet } from "react-router-dom";
import { DocsNavSidebar } from "./DocsNavSidebar";
import { DocsAuthProvider, useDocsAuth } from "../hooks/useDocsAuth";
import { getCategoryLabel, KNOWN_CATEGORY_ORDER, type DocCategoryGroup, type DocsInventoryPage } from "../types";
import { LogOut } from "lucide-react";
import { Link } from "react-router-dom";
import fullLogo from "@/assets/logos/full-logo.svg";

// ---------------------------------------------------------------------------
// Inner layout (needs auth context)
// ---------------------------------------------------------------------------

function DocsLayoutInner() {
  const { user, isLoading, isInternal, logout, scope, folderId } = useDocsAuth();
  const brandIsClickable = scope !== "page";
  const [pages, setPages] = useState<DocsInventoryPage[]>([]);

  useEffect(() => {
    if (isLoading) return;
    if (!user) return;

    let cancelled = false;

    async function fetchInventory() {
      try {
        const token = localStorage.getItem("docs_jwt");
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;

        const res = await fetch("/api/docs/inventory", { headers });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setPages(data.pages);
        }
      } catch {
        // Sidebar will just be empty
      }
    }

    fetchInventory();
    return () => { cancelled = true; };
  }, [user, isLoading]);

  const categories = useMemo((): DocCategoryGroup[] => {
    // Discover all labels present in the inventory
    const allLabels = new Set(pages.flatMap((p) => p.labels));

    // Known labels first (in preferred order), then any new labels alphabetically
    const knownSet = new Set<string>(KNOWN_CATEGORY_ORDER);
    const newLabels = [...allLabels].filter((l) => !knownSet.has(l)).sort();
    const categoryOrder = [...KNOWN_CATEGORY_ORDER.filter((c) => allLabels.has(c)), ...newLabels];

    const groups: DocCategoryGroup[] = [];

    for (const cat of categoryOrder) {
      const items = pages
        .filter((p) => p.labels.includes(cat))
        .map((p) => ({
          slug: p.id,
          title: p.title,
          category: cat,
        }))
        .sort((a, b) => {
          const sortKey = (t: string) => t.replace(/^Explainer:\s*/i, "").toLowerCase();
          return sortKey(a.title).localeCompare(sortKey(b.title));
        });
      if (items.length > 0) {
        groups.push({
          category: cat,
          label: getCategoryLabel(cat),
          items,
        });
      }
    }
    return groups;
  }, [pages]);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-ev-surface">
      {/* Brand gradient accent stripe */}
      <div className="h-1 bg-ev-gradient" aria-hidden="true" />

      {/* Header — Evermore branding */}
      <header className="shrink-0 border-b border-ev-border bg-ev-surface">
        <div className="flex h-16 items-center justify-between px-6">
          {brandIsClickable ? (
            <Link to="/docs" className="flex items-center gap-4 transition-opacity hover:opacity-80">
              <img
                src={fullLogo}
                alt="Evermore"
                className="h-10 w-auto"
              />
              <div className="hidden h-8 w-px bg-ev-border sm:block" />
              <span className="hidden text-base font-semibold text-ev-text-primary sm:inline">
                evermore Product Documentation
              </span>
            </Link>
          ) : (
            <div className="flex items-center gap-4" aria-label="Evermore Product Documentation">
              <img
                src={fullLogo}
                alt="Evermore"
                className="h-10 w-auto"
              />
              <div className="hidden h-8 w-px bg-ev-border sm:block" />
              <span className="hidden text-base font-semibold text-ev-text-primary sm:inline">
                evermore Product Documentation
              </span>
            </div>
          )}
          {user && isInternal && (
            <div className="flex items-center gap-3">
              <span className="hidden text-xs text-ev-text-muted sm:inline">
                {user.email}
              </span>
              <button
                onClick={logout}
                className="flex items-center gap-1 rounded px-2 py-1 text-xs text-ev-text-muted transition-colors hover:bg-ev-surface-subtle hover:text-ev-text-primary"
                title="Sign out"
              >
                <LogOut className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Body — sidebar + content */}
      <div className="flex flex-1 overflow-hidden">
        {/* scope="page" → no sidebar; scope="folder" → single-folder sidebar; all others → full sidebar */}
        {user && categories.length > 0 && scope !== "page" && (
          <DocsNavSidebar
            categories={categories}
            isInternal={isInternal}
            scopedFolderId={scope === "folder" ? folderId : undefined}
          />
        )}

        {/* Main content area */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-4xl px-6 py-8 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DocsLayout — wraps with auth provider
// ---------------------------------------------------------------------------

export function DocsLayout() {
  return (
    <DocsAuthProvider>
      <DocsLayoutInner />
    </DocsAuthProvider>
  );
}
